/**
 * Overview tab — displays 4 metrics stat cards for the loyalty program.
 *
 * Plan 177, Epic 07 — Admin Management & Analytics
 */

import { Users, TrendingUp, Gift, Percent } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useRewardsMetrics } from "../api/rewardsAdminHooks";
import { ExpiryLogTab } from "./ExpiryLogTab";

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  iconColor: string;
  iconBg: string;
}

function StatCard({ icon: Icon, label, value, iconColor, iconBg }: StatCardProps) {
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconBg}`}>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div>
        <p className="text-2xl font-semibold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3">
      <Skeleton className="h-9 w-9 rounded-lg" />
      <div className="space-y-1">
        <Skeleton className="h-7 w-16" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  );
}

export function OverviewTab() {
  const metricsQuery = useRewardsMetrics();
  const m = metricsQuery.data;

  if (metricsQuery.isLoading || !m) {
    return (
      <div className="grid grid-cols-2 gap-4 pt-4 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const allZero = m.total_members === 0 && m.points_issued === 0;

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          icon={Users}
          label="สมาชิก"
          value={m.total_members.toLocaleString()}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <StatCard
          icon={TrendingUp}
          label="แต้มที่ออก"
          value={m.points_issued.toLocaleString()}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <StatCard
          icon={Gift}
          label="แต้มที่แลก"
          value={m.points_redeemed.toLocaleString()}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <StatCard
          icon={Percent}
          label="อัตราการแลก"
          value={`${m.redemption_rate}%`}
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
        />
      </div>

      {allZero && (
        <p className="text-sm text-muted-foreground text-center py-4">
          แต้มจะเริ่มสะสมเมื่อลูกค้าเริ่มซื้อสินค้า
        </p>
      )}

      {/* Expiry activity log */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
          กิจกรรมหมดอายุล่าสุด
        </p>
        <ExpiryLogTab />
      </div>
    </div>
  );
}
